import React from "react"
import { FormContainer, Worksheet, CheckboxYesNo } from "uikit"
import { Grid, Select } from "semantic-ui-react"
import { goalTypes } from "../../goalTypes"
import { Footer } from "./Footer"
import { useStore } from "store"

const PersonForm = ({
  person,
  formData: {
    fields: { gender, health_status, is_tobacco_user }
  },
  genderOptions,
  healthStatusOptions,
  tobaccoUserOptions
}) => (
  <Grid.Row>
    <Grid.Column>
      <Grid stackable>
        <Worksheet.Divider label={person.name} />
        <Worksheet.Body>
          <Worksheet.Row label={`What is ${person.name}'s gender?`} paddedField>
            <Worksheet.Field>
              <Select {...gender} options={genderOptions} fluid />
            </Worksheet.Field>
          </Worksheet.Row>
          <Worksheet.Row label={`How is ${person.name}'s health?`} paddedField>
            <Worksheet.Field>
              <Select {...health_status} options={healthStatusOptions} fluid />
            </Worksheet.Field>
          </Worksheet.Row>
          <Worksheet.Row
            label={`Is ${person.name} a tobacco user?`}
            paddedField
          >
            <Worksheet.Field>
              <Select {...is_tobacco_user} options={tobaccoUserOptions} fluid />
            </Worksheet.Field>
          </Worksheet.Row>
        </Worksheet.Body>
      </Grid>
    </Grid.Column>
  </Grid.Row>
)

const LifeInsuranceForm = ({
  data: {
    fields: { expenses_coverage, goal_coverage, covered_goals, people },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  filteredPeople,
  expensesPercentOptions,
  insureGoalOptions,
  tobaccoUserOptions,
  genderOptions,
  healthStatusOptions,
  insurableGoals,
  updateCoveredGoals,
  isNew,
  getPersonName
}) => (
  <Worksheet error={error}>
    <Worksheet.Body>
      <Worksheet.Row
        label="What portion of your living expenses would you like to insure?"
        paddedField
      >
        <Worksheet.Field>
          <Select
            {...expenses_coverage}
            fluid
            options={expensesPercentOptions}
          />
        </Worksheet.Field>
      </Worksheet.Row>
      <Worksheet.Row label="Would you like to insure your goals?" paddedField>
        <Worksheet.Field>
          <Select {...goal_coverage} fluid options={insureGoalOptions} />
        </Worksheet.Field>
        {goal_coverage.value === "some" ? (
          <Worksheet.RowCollection>
            {insurableGoals.map(goal => (
              <Worksheet.Row
                key={goal.id}
                label={
                  goal.personId
                    ? `Insure ${goal.name} goal for ${getPersonName(
                        goal.personId
                      )}?`
                    : `Insure your ${goal.name} goal?`
                }
              >
                <Worksheet.Field>
                  <CheckboxYesNo
                    fluid
                    name={goal.id}
                    checked={
                      !!covered_goals.array.find(
                        element => element.fields.goal_id.value === goal.id
                      )
                    }
                    onChange={(ev, data) =>
                      updateCoveredGoals(covered_goals, goal.id, data.value)
                    }
                  />
                </Worksheet.Field>
              </Worksheet.Row>
            ))}
          </Worksheet.RowCollection>
        ) : null}
      </Worksheet.Row>
    </Worksheet.Body>
    {filteredPeople.map(person => (
      <PersonForm
        key={person.person_id}
        person={person}
        formData={people.array.find(
          goal => goal.fields.person_id.value === person.person_id
        )}
        genderOptions={genderOptions}
        healthStatusOptions={healthStatusOptions}
        tobaccoUserOptions={tobaccoUserOptions}
      />
    ))}

    <Footer
      baseUrl={baseUrl}
      isNew={isNew}
      submitLabel={submitLabel}
      onSubmit={onSubmit}
      isPending={isPending}
      onDelete={onDelete}
      isModified={isModified}
      deleteLabel="Delete Goal"
      error={error}
    />
  </Worksheet>
)

const LifeInsuranceFormContainer = FormContainer(
  {
    expenses_coverage: {
      type: "unit",
      minValue: 10
    },
    goal_coverage: {
      type: "string" // all, some, none
    },
    covered_goals: {
      type: "array",
      schema: {
        goal_id: {
          type: "string"
        }
      }
    },
    people: {
      type: "array",
      schema: {
        person_id: {
          type: "string"
        },
        gender: {
          type: "string"
        },
        health_status: {
          type: "string"
        },
        is_tobacco_user: {
          type: "boolean"
        }
      }
    },
    type: {
      type: "string",
      defaultValue: "lifeinsurance"
    }
  },
  (fields, ...form) => {}
)(LifeInsuranceForm)

const LifeInsurance = ({ household, scenario, goal, ...formProps }) => {
  const [people] = useStore("householdPeople", household.id)
  const [
    goalsObject,
    { addGoal, updatePerson, updateGoal, deleteGoal }
  ] = useStore("scenarioGoals", scenario.id)

  const goals = Object.values(goalsObject)
  const isNew = goal === null
  const expensesPercentOptions = [
    { value: "50", text: "50%" },
    { value: "75", text: "75%" },
    { value: "100", text: "100%" }
  ]

  const insureGoalOptions = [
    { value: "all", text: "Yes" },
    { value: "none", text: "No" }
  ]

  const genderOptions = [
    { value: "male", text: "Male" },
    { value: "female", text: "Female" }
  ]

  const healthStatusOptions = [
    { value: "excellent", text: "Excellent" },
    { value: "good", text: "Good" },
    { value: "fair", text: "Fair" },
    { value: "poor", text: "Poor" }
  ]

  const tobaccoUserOptions = [
    { value: "false", text: "No" },
    { value: "true", text: "Yes" }
  ]

  const filteredPeople = Object.values(people)
    .filter(person => person.role === "client" || person.role === "spouse")
    .map(person => ({
      person_id: person.id,
      name: person.first_name,
      gender: person.gender || "",
      health_status: person.health_status || "",
      is_tobacco_user: person.is_tobacco_user || ""
    }))

  const insurableGoalTypes = Object.keys(goalTypes)
    .filter(key => goalTypes[key].insurable)
    .map(key => key)
  const insurableGoals = goals
    .filter(goal => insurableGoalTypes.includes(goal.type))
    .map(goal => ({
      id: goal.id,
      name:
        goal.type === "other" ? goal.description : goalTypes[goal.type].label,
      personId: goal.person_id
    }))
  if (insurableGoals.length > 0) {
    insureGoalOptions.push({ value: "some", text: "Some" })
  }

  const getPersonName = personId => {
    let person = Object.values(people).find(person => person.id === personId)
    if (person) return person.first_name
    else return "you"
  }
  const updateCoveredGoals = (covered_goals, goalId, add) => {
    if (add) {
      if (
        !covered_goals.array.find(
          element => element.fields.goal_id.value === goalId
        )
      ) {
        covered_goals.append({ goal_id: goalId })
      }
    } else {
      let element = covered_goals.array.find(
        element => element.fields.goal_id.value === goalId
      )
      element && element.remove()
    }
  }
  let values = {}

  Object.assign(values, {
    people: filteredPeople
  })
  values = { people: filteredPeople }
  if (isNew) {
    values = { people: filteredPeople }
  } else {
    values = Object.assign({}, goal, { people: filteredPeople })
  }
  const submit = isNew
    ? async ({ people, ...goalData }) => {
        Object.assign(goalData, {
          covered_goals:
            goalData.goal_coverage === "some" ? goalData.covered_goals : []
        })
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...goalData
        })
        const personUpdates = people.map(({ person_id, ...personData }) => {
          return updatePerson({
            id: person_id,
            ...personData
          })
        })
        await Promise.all(personUpdates)
      }
    : async ({ people, ...goalData }) => {
        Object.assign(goalData, {
          covered_goals:
            goalData.goal_coverage === "some" ? goalData.covered_goals : []
        })
        await updateGoal({ id: goal.id, ...goalData })
        const personUpdates = people.map(({ person_id, ...personData }) => {
          return updatePerson({
            id: person_id,
            ...personData
          })
        })
        await Promise.all(personUpdates)
      }
  const onDelete = async () => {
    await deleteGoal({ id: goal.id, scenario_id: scenario.id })
    return true
  }
  return (
    <LifeInsuranceFormContainer
      {...{
        values,
        submit,
        onDelete,
        filteredPeople,
        expensesPercentOptions,
        insureGoalOptions,
        tobaccoUserOptions,
        genderOptions,
        healthStatusOptions,
        insurableGoals,
        updateCoveredGoals,
        getPersonName,
        ...formProps
      }}
    />
  )
}

export default LifeInsurance
